<template>
  <login-layout
    :title="invite?.azienda ? $t('Registrati per accettare l\'invito di {0}', [invite.azienda]) : $t('Crea il tuo Account Artera')"
  >
    <pf-content v-if="invite?.azienda">
      {{
        $t("Effettuando la registrazione verrai collegato all'account di {0} e sarai abilitato a:", [invite.azienda])
      }}
      <ul>
        <li v-if="invite.grants">{{ $t('Accedere alla sua area personale dalla tua utenza') }}</li>
        <li v-if="invite.email_subscriptions?.includes('technical')">
          {{ $t("Ricevere in copia le notifiche tecniche dell'azienda") }}
        </li>
        <li v-if="invite.email_subscriptions?.includes('billing')">
          {{ $t("Ricevere in copia le notifiche amministrative dell'azienda") }}
        </li>
        <li v-if="invite.email_subscriptions?.includes('marketing')">
          {{ $t("Ricevere in copia le notifiche commerciali dell'azienda") }}
        </li>
      </ul>
    </pf-content>

    <x-form
      :disabled="sending"
      :submit-text="app.isInIframe ? $t('Iscriviti ora') : $t('Procedi')"
      submit-block
      @submit="submit"
    >
      <form-group :name="$t('Nome e cognome / Ragione Sociale')" no-label>
        <x-input
          id="signupRagSoc"
          v-model="name"
          type="text"
          name="ragione_sociale"
          :placeholder="$t('Nome e cognome / Ragione Sociale')"
          required
          autocomplete="organization"
        />
      </form-group>

      <form-group :name="$t('Email')" no-label>
        <x-input
          id="signupEmail"
          v-model="email"
          type="email"
          name="email"
          :placeholder="$t('Email')"
          required
          :readonly="!!invite?.azienda || emailReadOnly"
          autocomplete="email"
        />
        <input v-if="$route.query.verified" type="hidden" name="verified" :value="$route.query.verified">
      </form-group>

      <form-group :name="$t('Password')" no-label>
        <x-input
          id="signupPassword"
          type="password"
          name="password"
          :placeholder="$t('Password')"
          required
          autocomplete="new-password"
          minlength="8"
        />
      </form-group>

      <pf-form-group>
        <pf-checkbox name="ack_privacy" required>
          <template #label>
            <i18n-t keypath="Accetto la {0}" tag="span">
              <a href="https://www.artera.net/it/informativa-privacy/" target="_blank" rel="noopener">{{
                $t('Privacy Policy')
              }}</a>.
            </i18n-t>
          </template>
        </pf-checkbox>
      </pf-form-group>

      <pf-form-group>
        <pf-checkbox name="ack_marketing" :label="$t('Accetto di ricevere altri messaggi: notizie, contenuti didattici e contenuti relativi ai prodotti offerti')" />
      </pf-form-group>
    </x-form>

    <div v-if="!emailReadOnly" ref="scriptInjectElement" class="login-buttons">
      <div class="separator">
        <span>{{ $t('oppure') }}</span>
      </div>

      <div ref="googleLoginButton" />

      <a v-if="linkedInLoginUrl" :href="linkedInLoginUrl" class="btn-linkedin btn-block btn-lg" :class="{ disabled: sending }" @click="sending ? $event.preventDefault() : null">
        <img src="@/assets/linkedin-in.png" width="30" height="30" alt="LinkedIn" style="margin-right: 4px">
        <span>
          {{ $t('Accedi con LinkedIn') }}
        </span>
      </a>
    </div>

    <template v-if="!app.isInIframe" #forgot-credentials>
      {{ $t('Hai già un account?') }}
      <router-link :to="{ name: 'login' }">{{ $t('Log In') }}</router-link>
    </template>
  </login-layout>
</template>

<script lang="ts" setup>
import { http } from '../../http';
import { asyncComputed } from '@common/asyncComputed';
import { loginDestination } from '../../utils';
import LoginLayout from '../../components/login-layout.vue';
import { Resource } from '@/resources';
import { onMounted, ref, useTemplateRef } from 'vue';
import type { Invite } from '@/typings/resources';
import { useAppStore } from '@/store/app';
import { useRoute, useRouter } from 'vue-router';
import { injectScript, pop } from '@common/utils';
import { useOauthUrls } from './use';

defineOptions({
  name: 'SignupPage',
});

const app = useAppStore();
const route = useRoute();
const router = useRouter();

const name = ref(pop(route.query.name) ?? '');
const email = ref(pop(route.query.email) ?? '');

const emailReadOnly = ref(email.value !== '');

const googleLoginButton = useTemplateRef('googleLoginButton');
const scriptInjectElement = useTemplateRef('scriptInjectElement');

const { linkedInLoginUrl } = useOauthUrls();

const { result: invite } = asyncComputed(async() => {
  const antk = pop(route.query.antk);
  if (!antk) {
    return {};
  }

  const invite = await new Resource<Invite>('invites').get(antk);

  email.value = invite.email ?? '';
  emailReadOnly.value = true;
  invite.secret = antk;

  if (invite.username) {
    router.push({
      name: 'login',
      query: route.query,
    });
  }

  return invite;
}, null);

const sending = ref(false);

onMounted(async() => {
  try {
    const qRef = pop(route.query.ref);
    const afid = pop(route.query.afid);
    const aftk = pop(route.query.aftk);

    if (qRef) {
      localStorage.setItem('ref', qRef);
    }

    if (afid && aftk) {
      localStorage.setItem('afid', afid);
      localStorage.setItem('aftk', aftk);
    }
  } catch (e) {
    // ignore security error in case of block on third party cookies
    if (!(e instanceof DOMException) || e.name !== 'SecurityError') {
      throw e;
    }
  }

  initGoogleClient();
});

async function initGoogleClient() {
  if (typeof import.meta.env.GOOGLE_CLIENT_ID !== 'string' || !scriptInjectElement.value) {
    return;
  }

  try {
    await injectScript('https://accounts.google.com/gsi/client', scriptInjectElement.value);
  } catch (e) {
    // Failed to load
    return;
  }

  const google: typeof import('google-one-tap') | undefined = (window as any).google;
  if (!google) {
    return;
  }

  google.accounts.id.initialize({
    client_id: import.meta.env.GOOGLE_CLIENT_ID,
    callback: async({credential}: {credential: string}) => {
      const data = new FormData();
      data.append('google_token', credential);
      data.append('check', await fetchToken());

      const response = (await http.post('/api/auth/login', data)).data;
      if (response.result === 'ok') {
        const destination = loginDestination(route.query);
        if (app.isInIframe) {
          window.open(destination, '_blank');
        } else {
          router.push(destination);
        }
      } else if (response.name) {
        name.value = response.name;
        email.value = response.email;
        emailReadOnly.value = true;
      }
    },
  });

  if (googleLoginButton.value) {
    google.accounts.id.renderButton(googleLoginButton.value, { theme: "outline", size: "large" });
  }
}

async function submit(e: Event | undefined) {
  if (!(e?.target instanceof HTMLFormElement)) {
    return;
  }

  sending.value = true;

  try {
    const data = new FormData(e.target);
    data.append('check', await fetchToken());

    try {
      const afid = localStorage.getItem('afid');
      const aftk = localStorage.getItem('aftk');
      if (afid && aftk) {
        data.append('afid', afid);
        data.append('aftk', aftk);
      }
    } catch (e) {
      // ignore security error in case of block on third party cookies
      if (!(e instanceof DOMException) || e.name !== 'SecurityError') {
        throw e;
      }
    }

    if (invite.value?.secret) {
      data.append('antk', invite.value.secret);
    }

    const prospect_source = pop(route.query.prospect_source);
    if (prospect_source) {
      data.append('prospect_source', prospect_source);
    }

    const response = await http.post('/api/auth/signup', data);

    let url: URL | null = null;
    if (app.isInIframe) {
      window.open(`https://www.artera.net/it/thankyou-admin?prospect_source=${prospect_source}`, '_blank');
      return;
    } else if(response.data.goto) {
      url = new URL(response.data.goto, window.location.href);
    } else if (response.data.result == 'ok') {
      router.push(loginDestination(route.query));
      return;
    }

    if (!url) {
      throw new Error('Invalid response');
    }

    const qRef = pop(route.query.ref);
    if (qRef) {
      url.searchParams.append('ref', qRef);
    }

    if (prospect_source) {
      url.searchParams.append('prospect_source', prospect_source);
    }

    router.push(url.toString().substring(url.origin.length));
  } finally {
    sending.value = false;
  }
}

async function fetchToken() {
  return (await http.post('/api/auth/token')).data.token;
}
</script>
